/**
 * Los enlaces, como piezas: ficha al pasar el mouse y edición por UI.
 *
 * En la hoja un enlace se ve como un enlace y clickearlo NAVEGA. El markdown de
 * abajo (`[texto](destino)`) no aparece nunca y no se edita a mano: el cursor
 * lo saltea (ver los rangos atómicos en `livePreview.ts`) y para tocarlo está
 * su ficha —la que asoma al pasar el mouse—, que muestra a dónde va y ofrece
 * abrirlo, editarlo o quitarlo.
 *
 * Es lo que destraba el problema de fondo: mientras el marcado volvía al poner
 * el cursor encima, un enlace era o navegable o editable, nunca las dos, y
 * clickearlo no podía llevar a ningún lado sin pisarle el gesto de escribir.
 *
 * Lo que se escribe en el `.md` sigue siendo markdown estándar. Un
 * `[[wikilink]]` de un vault importado se lee y se navega igual, pero al
 * editarlo desde la ficha se guarda como enlace markdown: Blanqo no escribe
 * wikilinks (ver `vault/links.ts`).
 */

import { type Command, hoverTooltip } from "@codemirror/view";
import { type Extension } from "@codemirror/state";
import { openLinkDialog } from "../app/dialogs.ts";
import {
  encodeTarget,
  hrefFor,
  isExternalHref,
  linkLabel,
  normalizeLinkDestination,
  splitHash,
} from "../vault/links.ts";
import { linkAt } from "./livePreview.ts";
import { type LinkMenuOptions } from "./linkMenu.ts";

export interface LinkUIOptions extends LinkMenuOptions {
  /** Ruta del vault a la que lleva un destino, o null si no existe. */
  resolveLink?: (href: string) => string | null;
  /** Abrir el destino (otro documento, un ancla, o afuera del navegador). */
  open?: (href: string) => void;
}

/**
 * Lo que hay que escribir como destino para lo que el usuario eligió.
 *
 * Una URL externa o un ancla van tal cual; una ruta del vault se escribe
 * RELATIVA al documento actual, que es lo que hace que el enlace siga andando
 * si después se mueve la carpeta entera.
 */
function toHref(value: string, docPath: string, targets: string[]): string {
  const raw = normalizeLinkDestination(value, targets);
  if (raw === "" || isExternalHref(raw) || raw.startsWith("#")) return raw;
  const { target, hash } = splitHash(raw);
  const decoded = decodeURIComponent(target);
  if (targets.includes(decoded)) return hrefFor(docPath, decoded, hash);
  return `${encodeTarget(target)}${hash}`;
}

/**
 * El markdown de un enlace, con el destino ya traducido.
 *
 * Lo usan los dos lugares donde se escribe un enlace —la hoja y una celda de
 * tabla— para que una ruta con espacios o una carpeta lejana se escriban igual
 * en los dos.
 */
export function linkMarkdown(
  text: string,
  href: string,
  docPath: string,
  targets: string[],
): string {
  const dest = toHref(href, docPath, targets);
  return `[${text || labelFor(dest)}](${dest})`;
}

/** El texto por defecto de un enlace recién creado: el nombre de su destino. */
function labelFor(href: string): string {
  const { target } = splitHash(href);
  if (isExternalHref(href)) return href;
  return linkLabel(decodeURIComponent(target)) || href;
}

/**
 * Crea un enlace, o edita el que está debajo del cursor.
 *
 * Lo usan `/link` y ⌘K. Si hay texto seleccionado, ese texto entra como nombre:
 * el gesto de "escribo la frase y después la enlazo" no se pierde.
 */
export function linkCommand(opts: LinkUIOptions): Command {
  return (view) => {
    const sel = view.state.selection.main;
    const existing = linkAt(view.state, sel.from);
    const selected = view.state.sliceDoc(sel.from, sel.to);
    const targets = opts.targets();

    void (async () => {
      const res = await openLinkDialog({
        text: existing?.text ?? selected,
        href: existing?.href ?? "",
        targets,
        existing: existing !== null,
      });
      if (!res) return;

      const from = existing?.from ?? sel.from;
      const to = existing?.to ?? sel.to;
      if (res.action === "remove") {
        // Quitar el enlace deja el texto: lo que se saca es el destino, no lo
        // que la frase decía.
        const insert = existing?.text ?? "";
        view.dispatch({ changes: { from, to, insert }, selection: { anchor: from + insert.length } });
        view.focus();
        return;
      }

      const insert = linkMarkdown(res.text, res.href, opts.docPath(), targets);
      view.dispatch({
        changes: { from, to, insert },
        selection: { anchor: from + insert.length },
        userEvent: "input",
      });
      view.focus();
    })();
    return true;
  };
}

/** Lo que la ficha muestra de un enlace. */
export interface LinkCardData {
  text: string;
  href: string;
  /** Un destino interno que todavía no existe. */
  missing: boolean;
}

export interface LinkCardActions {
  open: () => void;
  edit: () => void;
  remove: () => void;
}

/** Un enlace roto es uno interno (ni externo ni ancla) que no resuelve. */
export function isMissing(href: string, resolve?: (h: string) => string | null): boolean {
  if (href.startsWith("#") || resolve?.(href)) return false;
  return !isExternalHref(normalizeLinkDestination(href));
}

/**
 * La ficha del enlace: qué dice, a dónde va, y qué se puede hacer con él.
 *
 * La comparten la hoja y las celdas de una tabla —los dos dibujan enlaces— para
 * que un enlace se vea y se toque igual esté donde esté.
 */
export function buildLinkCard(data: LinkCardData, actions: LinkCardActions): HTMLElement {
  const dom = document.createElement("div");
  dom.className = "cm-link-card";

  const name = document.createElement("div");
  name.className = "cm-link-card-name";
  name.textContent = data.text || labelFor(data.href);

  const dest = document.createElement("div");
  dest.className = "cm-link-card-dest";
  dest.textContent = decodeURIComponent(data.href);
  if (data.missing) {
    dest.classList.add("is-missing");
    dest.title = "No existe todavía";
  }

  const row = document.createElement("div");
  row.className = "cm-link-card-actions";
  // Los mismos botones que los diálogos de la app (`btn-primary`/`btn-ghost`),
  // en su versión compacta: la ficha y el cuadro de "nuevo enlace" son la misma
  // herramienta vista de dos maneras, y tienen que parecerlo.
  const button = (label: string, cls: string, run: () => void): void => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = `${cls} is-compact`;
    b.textContent = label;
    b.addEventListener("mousedown", (e) => {
      e.preventDefault();
      e.stopPropagation();
      run();
    });
    row.append(b);
  };
  button(data.missing ? "Create" : "Open", "btn-primary", actions.open);
  button("Edit", "btn-ghost", actions.edit);
  button("Remove", "btn-ghost is-danger", actions.remove);

  dom.append(name, dest, row);
  return dom;
}

/**
 * La ficha flotando junto a un elemento de la pantalla, para quien no puede
 * usar el tooltip de CodeMirror: adentro de una tabla los enlaces son DOM del
 * widget, no texto del documento, así que el hover del editor no los ve.
 *
 * No va dentro de la tabla —que recorta lo que se sale de su caja— pero TAMPOCO
 * en el `body`: los colores del documento son variables declaradas en
 * `.md-view`, y afuera de ahí la ficha salía transparente y con el texto
 * ilegible. Cuelga de la vista y se posiciona `fixed`, que no la recorta nadie.
 */
export function floatLinkCard(
  target: HTMLElement,
  data: LinkCardData,
  actions: LinkCardActions,
): { dom: HTMLElement; close: () => void } {
  const card = buildLinkCard(data, actions);
  card.classList.add("is-floating");
  (target.closest(".md-view") ?? document.body).append(card);

  const place = (): void => {
    const r = target.getBoundingClientRect();
    const w = card.offsetWidth;
    card.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - w - 8))}px`;
    card.style.top = `${Math.max(8, r.top - card.offsetHeight - 6)}px`;
  };
  place();

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    card.remove();
    window.removeEventListener("scroll", close, true);
    window.removeEventListener("resize", close);
  };
  window.addEventListener("scroll", close, true);
  window.addEventListener("resize", close);
  // Pasar el mouse a la ficha no la cierra: hay botones que apretar.
  card.addEventListener("mouseleave", close);
  return { dom: card, close };
}

/** La ficha aparece al dejar el mouse sobre un enlace. */
export function linkHover(opts: LinkUIOptions): Extension {
  return hoverTooltip(
    (view, pos) => {
      const link = linkAt(view.state, pos);
      if (!link || pos < link.from || pos > link.to) return null;
      return {
        pos: link.from,
        end: link.to,
        above: true,
        create: () => ({
          dom: buildLinkCard(
            { text: link.text, href: link.href, missing: isMissing(link.href, opts.resolveLink) },
            {
              open: () => opts.open?.(link.href),
              edit: () => {
                // El comando trabaja sobre el enlace que hay en el cursor: se
                // lo lleva ahí primero, así editar desde la ficha y editar con
                // el comando son el mismo camino.
                view.dispatch({ selection: { anchor: link.from } });
                linkCommand(opts)(view);
              },
              remove: () => {
                view.dispatch({
                  changes: { from: link.from, to: link.to, insert: link.text },
                  selection: { anchor: link.from + link.text.length },
                });
                view.focus();
              },
            },
          ),
        }),
      };
    },
    { hoverTime: 250 },
  );
}
